:host {
  --widget-primary: #0F766E;
  --widget-bg: #ffffff;
  --widget-text: #0a0a0a;
  --widget-muted: #64748b;
  --widget-border: #e2e8f0;
  --widget-radius: 12px;
  --widget-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08);
  --widget-z: 2147483647;

  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: var(--widget-text);
}

* {
  box-sizing: border-box;
}

span {}

button {
  font: inherit;
  color: inherit;
  cursor: pointer;
  border: none;
  background: none;
  padding: 0;
}

input,
textarea {
  font: inherit;
  color: inherit;
}

.widget-root {
  position: fixed;
  bottom: 24px;
  z-index: var(--widget-z);
}

.widget-root.position-right {
  right: 24px;
}

.widget-root.position-left {
  left: 24px;
}

/* Launcher stack (launcher + optional greeting bubble) */

.widget-launcher-stack {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-end;
}

.widget-root.position-left .widget-launcher-stack {
  align-items: flex-start;
}

/* Greeting bubble */

.widget-greeting {
  position: relative;
  max-width: 280px;
  padding: 12px 36px 12px 14px;
  background: var(--widget-bg);
  border: 1px solid var(--widget-border);
  border-radius: var(--widget-radius);
  box-shadow: var(--widget-shadow);
  animation: widget-greeting-in 200ms ease-out;
}

@keyframes widget-greeting-in {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

.widget-greeting-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--widget-text);
}

.widget-greeting-close {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: var(--widget-muted);
  transition: background 120ms, color 120ms;
}

.widget-greeting-close:hover {
  background: #f1f5f9;
  color: var(--widget-text);
}

.widget-greeting-close svg {
  width: 14px;
  height: 14px;
}

/* Launcher */

.widget-launcher {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 9999px;
  background: var(--widget-primary);
  color: #ffffff;
  box-shadow: var(--widget-shadow);
  transition: transform 120ms ease-out, background 120ms ease-out;
}

.widget-launcher:hover {
  background: color-mix(in srgb, var(--widget-primary) 82%, black);
  transform: translateY(-2px);
}

.widget-launcher:active {
  transform: translateY(0);
}

.widget-launcher svg {
  width: 24px;
  height: 24px;
}

/* Panel */

.widget-panel {
  width: 380px;
  max-width: calc(100vw - 48px);
  height: 720px;
  max-height: calc(100vh - 48px - 56px - 12px);
  background: var(--widget-bg);
  border-radius: 0 0 var(--widget-radius) var(--widget-radius);
  box-shadow: var(--widget-shadow);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: widget-panel-in 160ms ease-out;
}

@keyframes widget-panel-in {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.widget-panel-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: var(--widget-primary);
  color: #ffffff;
}

.widget-panel-header-logo {
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.widget-panel-header-logo img {
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
}

.widget-panel-header-text {
  flex: 1;
  min-width: 0;
}

.widget-panel-header-title {
  font-weight: 600;
  font-size: 15px;
  line-height: 1.2;
}

.widget-panel-header-agent {
  font-size: 12px;
  opacity: 0.85;
  margin-top: 2px;
}

.widget-panel-header-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  color: #ffffff;
  opacity: 0.85;
  transition: opacity 120ms, background 120ms;
}

.widget-panel-header-close:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.15);
}

.widget-panel-header-close svg {
  width: 18px;
  height: 18px;
}

.widget-panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #f8fafc;
}

.widget-panel-body-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--widget-muted);
  text-align: center;
  font-size: 13px;
}

.widget-messages {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.widget-message {
  display: flex;
  width: 100%;
}

.widget-message-user {
  justify-content: flex-end;
}

.widget-message-agent {
  justify-content: flex-start;
}

.widget-message-column {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 85%;
}

.widget-message-column-wide {
  max-width: 100%;
  width: 100%;
}

.widget-message-bubble {
  max-width: 100%;
  padding: 8px 12px;
  border-radius: 2px;
  font-size: 1.15em;
  line-height: 1.5;
  word-wrap: break-word;
}

.widget-message-user .widget-message-bubble {
  background: var(--widget-primary);
  color: #ffffff;
}

.widget-message-agent .widget-message-bubble {
  background: #ffffff;
  border: 1px solid var(--widget-border);
  color: var(--widget-text);
}

.widget-message-agent .widget-message-bubble p {
  margin: 0 0 8px;
}

.widget-message-agent .widget-message-bubble p:last-child {
  margin-bottom: 0;
}

.widget-message-agent .widget-message-bubble ul,
.widget-message-agent .widget-message-bubble ol {
  margin: 6px 0;
  padding-left: 20px;
}

.widget-message-agent .widget-message-bubble li {
  margin-bottom: 2px;
}

.widget-message-agent .widget-message-bubble a {
  color: var(--widget-primary);
  text-decoration: underline;
}

.widget-message-agent .widget-message-bubble code {
  background: #f1f5f9;
  padding: 1px 4px;
  border-radius: 3px;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 0.9em;
}

.widget-message-agent .widget-message-bubble pre {
  background: #f1f5f9;
  padding: 8px 12px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 8px 0;
}

.widget-message-agent .widget-message-bubble pre code {
  background: none;
  padding: 0;
}

.widget-message-agent .widget-message-bubble h1,
.widget-message-agent .widget-message-bubble h2,
.widget-message-agent .widget-message-bubble h3 {
  margin: 10px 0 4px;
  font-weight: 600;
}

.widget-message-agent .widget-message-bubble h1:first-child,
.widget-message-agent .widget-message-bubble h2:first-child,
.widget-message-agent .widget-message-bubble h3:first-child {
  margin-top: 0;
}

.widget-message-agent .widget-message-bubble h1 { font-size: 16px; }
.widget-message-agent .widget-message-bubble h2 { font-size: 15px; }
.widget-message-agent .widget-message-bubble h3 { font-size: 14px; }

.widget-message-agent .widget-message-bubble .widget-message-table-wrap {
  margin: 8px 0;
  overflow-x: auto;
}

.widget-message-agent .widget-message-bubble table {
  border-collapse: collapse;
  font-size: 0.9em;
  white-space: nowrap;
}

.widget-message-agent .widget-message-bubble th,
.widget-message-agent .widget-message-bubble td {
  padding: 4px 8px;
  border: 1px solid var(--widget-border);
  text-align: left;
}

.widget-message-agent .widget-message-bubble th {
  background: #f1f5f9;
  font-weight: 600;
}

.widget-typing {
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 10px 12px;
}

.widget-typing-dot {
  width: 6px;
  height: 6px;
  background: var(--widget-muted);
  border-radius: 9999px;
  animation: widget-typing-bounce 1.4s infinite ease-in-out;
}

.widget-typing-dot:nth-child(2) { animation-delay: 160ms; }
.widget-typing-dot:nth-child(3) { animation-delay: 320ms; }

@keyframes widget-typing-bounce {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

.widget-panel-footer {
  border-top: 1px solid var(--widget-border);
  padding: 14px 16px;
  display: flex;
  gap: 12px;
  align-items: center;
  background: var(--widget-bg);
}

.widget-input {
  flex: 1;
  border: none;
  padding: 0;
  resize: none;
  min-height: 24px;
  max-height: 120px;
  outline: none;
  font-size: 14px;
  background: transparent;
  color: var(--widget-text);
}

.widget-input::placeholder {
  color: var(--widget-muted);
}

.widget-input:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.widget-send-button {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--widget-primary);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 120ms, opacity 120ms;
}

.widget-send-button:hover:not(:disabled) {
  background: color-mix(in srgb, var(--widget-primary) 82%, black);
}

.widget-send-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.widget-send-button svg {
  width: 16px;
  height: 16px;
}

/* Boat card component */

.boat-card {
  width: 100%;
  border: 1px solid var(--widget-border);
  border-radius: var(--widget-radius);
  background: var(--widget-bg);
  overflow: hidden;
  margin-top: 6px;
  display: flex;
  flex-direction: row;
}

.boat-card-image {
  width: 96px;
  flex-shrink: 0;
  object-fit: cover;
  display: block;
  background: #e2e8f0;
  align-self: stretch;
}

.boat-card-body {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.boat-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.boat-card-title {
  font-weight: 600;
  font-size: 12px;
  color: var(--widget-text);
  line-height: 1.3;
}

.boat-card-status {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 9999px;
}

.boat-card-status-in-stock {
  background: #dcfce7;
  color: #15803d;
}

.boat-card-status-on-order {
  background: #fef9c3;
  color: #a16207;
}

.boat-card-status-sold {
  background: #fee2e2;
  color: #b91c1c;
}

.boat-card-price {
  font-size: 15px;
  font-weight: 700;
  color: var(--widget-text);
}

.boat-card-location {
  font-size: 12px;
  color: var(--widget-muted);
}

.boat-card-highlights {
  margin: 2px 0 0;
  padding-left: 16px;
  font-size: 12px;
  color: var(--widget-muted);
  line-height: 1.6;
}

.boat-card-highlights li {
  margin-bottom: 1px;
}

.boat-card-cta {
  display: block;
  margin-top: 4px;
  padding: 8px 14px;
  background: var(--widget-primary);
  color: #ffffff;
  text-align: center;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  transition: background 120ms;
}

.boat-card-cta:hover {
  background: color-mix(in srgb, var(--widget-primary) 82%, black);
}

.loc-card {
  width: 100%;
  border: 1px solid var(--widget-border);
  border-radius: var(--widget-radius);
  background: var(--widget-bg);
  overflow: hidden;
  margin-top: 6px;
  display: flex;
  flex-direction: row;
}

.loc-card-body {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.loc-card-name {
  font-weight: 600;
  font-size: 13px;
  color: var(--widget-text);
  line-height: 1.3;
}

.loc-card-address {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12px;
  color: var(--widget-muted);
  line-height: 1.45;
}

.loc-card-pin {
  flex-shrink: 0;
  color: var(--widget-primary);
  margin-top: 1px;
}

.loc-card-pin svg {
  width: 13px;
  height: 13px;
  display: block;
}

.loc-card-actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.loc-card-cta {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  background: var(--widget-primary);
  color: #ffffff;
  text-align: center;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  transition: background 120ms, border-color 120ms;
}

.loc-card-cta:hover {
  background: color-mix(in srgb, var(--widget-primary) 82%, black);
}

.loc-card-cta-secondary {
  flex: 0 0 auto;
  background: transparent;
  color: var(--widget-primary);
  border: 1px solid var(--widget-border);
}

.loc-card-cta-secondary:hover {
  background: #f1f5f9;
  border-color: var(--widget-primary);
}

.loc-card-cta-icon svg {
  width: 14px;
  height: 14px;
  display: block;
}

.link-card {
  width: 100%;
  border: 1px solid var(--widget-border);
  border-radius: var(--widget-radius);
  background: var(--widget-bg);
  overflow: hidden;
  margin-top: 6px;
}

.link-card-body {
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.link-card-title {
  font-weight: 600;
  font-size: 13px;
  color: var(--widget-text);
  line-height: 1.3;
}

.link-card-description {
  font-size: 12px;
  color: var(--widget-muted);
  line-height: 1.45;
}

.link-card-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 4px;
  padding: 8px 12px;
  background: var(--widget-primary);
  color: #ffffff;
  text-align: center;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  transition: background 120ms, border-color 120ms;
}

.link-card-cta:hover {
  background: color-mix(in srgb, var(--widget-primary) 82%, black);
}

.link-card-cta-icon svg {
  width: 14px;
  height: 14px;
  display: block;
}

/* Mobile: full-screen panel instead of a floating card */

@media (max-width: 640px) {
  .widget-panel {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100dvh;
    max-width: 100vw;
    max-height: 100dvh;
    border-radius: 0;
  }

  /* Launcher always renders after the panel in the DOM (see App.tsx) — hide
     it while the full-screen panel is open rather than leaving it floating
     underneath. The close button in the panel header is the exit point. */
  .widget-panel ~ .widget-launcher {
    display: none;
  }
}
